import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Divider,
  Table,
  Tag,
  Space,
  Button,
  Descriptions,
  Alert,
  Steps,
  Progress,
} from "antd";
import {
  PrinterOutlined,
  DownloadOutlined,
  EyeOutlined,
  UserOutlined,
  CalculatorOutlined,
  MoneyCollectOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { formatCurrency, formatDate } from "../utils";
import { useAuth } from "../../../context/AuthContext";

const { Title, Text } = Typography;
const { Step } = Steps;

interface PayslipDetailsModalProps {
  visible: boolean;
  employeeId: number | null;
  period?: { id?: number } | null;
  periodId?: number | null;
  onCancel: () => void;
}

interface PayslipData {
  employee: {
    id: number;
    employee_number: string;
    first_name: string;
    last_name: string;
    professional_category: string;
    dependents_count: number;
    department_name: string;
    position_title: string;
    hire_date: string;
  };
  period: {
    name: string;
    start_date: string;
    end_date: string;
    company_name: string;
  };
  calculation_sequence: {
    totalRemuneration: number;
    cnssEmployeeAmount: number;
    grossSalary: number;
    exemptions: {
      housingExemption: number;
      responsibilityExemption: number;
      transportExemption: number;
      totalExemptions: number;
    };
    allowanceDeduction: number;
    netTaxableIncome: number;
    iutsZeroCharge: number;
    iutsNetToPay: number;
  };
  earnings: {
    basicSalary: number;
    totalRemuneration: number;
    grossSalary: number;
  };
  deductions: {
    cnss: {
      base: number;
      employeeRate: number;
      employeeAmount: number;
      employerAmount: number;
    };
    exemptions: {
      housingExemption: number;
      responsibilityExemption: number;
      transportExemption: number;
      totalExemptions: number;
    };
    allowanceDeduction: number;
    iuts: {
      taxableIncome: number;
      iutsZeroCharge: number;
      dependentsReduction: number;
      iutsNetToPay: number;
      reductionRate: number;
      dependentsCount: number;
      taxDetails?: Array<{
        bracket: string;
        rate: number;
        taxableAmount: number;
        taxAmount: number;
      }>;
    };
    totalDeductions: number;
  };
  netSalary: number;
  employerCosts: {
    cnssEmployer: number;
    totalEmployerCost: number;
  };
  calculatedAt: string;
  calculatorVersion: string;
  dataSource?: 'persisted' | 'recalculated';
  persistedAt?: string;
}

const PayslipDetailsModal: React.FC<PayslipDetailsModalProps> = ({
  visible,
  employeeId,
  period,
  periodId,
  onCancel,
}) => {
  const { token } = useAuth();
  const requestVersion = useRef(0);
  const [dataError, setDataError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [payslipData, setPayslipData] = useState<PayslipData | null>(null);

  // Déterminer l'ID de période à utiliser
  const resolvedPeriodId = period?.id || periodId;

  useEffect(() => {
    setPayslipData(null); setDataError(null);
    if (visible && employeeId && resolvedPeriodId) fetchPayslipData();
    return () => { requestVersion.current++; };
  }, [visible, employeeId, resolvedPeriodId]);

  // Afficher un message d'erreur si les données nécessaires sont manquantes
  if (visible && (!employeeId || !resolvedPeriodId)) {
    return (
      <Modal
        title="Bulletin de Paie - Erreur"
        open={visible}
        onCancel={onCancel}
        footer={[
          <Button key="close" onClick={onCancel}>
            Fermer
          </Button>
        ]}
        width={600}
      >
        <Alert
          message="Données manquantes"
          description="Impossible de charger le bulletin de paie. Les informations sur l'employé ou la période sont manquantes."
          type="error"
          showIcon
        />
      </Modal>
    );
  }

  const fetchPayslipData = async () => {
    const version = ++requestVersion.current;
    setLoading(true); setDataError(null);
    try {
      const response = await fetch(`/api/hr/payroll-processing/periods/${resolvedPeriodId}/employees/${employeeId}/payslip`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (requestVersion.current !== version) return;
      if (!response.ok || !data.success) throw new Error(data.error || 'Impossible de charger le bulletin enregistré');
      setPayslipData(data.data);
    } catch (error) {
      if (requestVersion.current === version) {
        setPayslipData(null); setDataError(error instanceof Error ? error.message : 'Impossible de charger le bulletin');
      }
    } finally { if (requestVersion.current === version) setLoading(false); }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // TODO: Implement PDF download
    console.log("Download PDF");
  };

  if (!payslipData && visible) {
    return (
      <Modal
        title="Bulletin de Paie IUTS"
        open={visible}
        onCancel={onCancel}
        footer={[
          <Button key="close" onClick={onCancel}>
            Fermer
          </Button>
        ]}
        width={800}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ marginBottom: 16 }}>
              <CalculatorOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </div>
            <Title level={4}>Chargement du bulletin de paie...</Title>
            <Text type="secondary">
              Chargement des montants et des détails du bulletin
            </Text>
          </div>
        ) : (
          <Alert
            message="Impossible de charger le bulletin"
            description={dataError || "Les données du bulletin n’ont pas pu être chargées. Réessayez."}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={fetchPayslipData}>
                Réessayer
              </Button>
            }
          />
        )}
      </Modal>
    );
  }

  // Vérification de sécurité - ne pas continuer si payslipData est null
  if (!payslipData) {
    return null;
  }

  const calculationSteps = [
    {
      title: "Rémunération Totale",
      description: formatCurrency(
        payslipData.calculation_sequence.totalRemuneration
      ),
      status: "finish" as const,
    },
    {
      title: "CNSS Employé",
      description: `-${formatCurrency(
        payslipData.calculation_sequence.cnssEmployeeAmount
      )}`,
      status: "finish" as const,
    },
    {
      title: "Salaire Brut",
      description: formatCurrency(payslipData.calculation_sequence.grossSalary),
      status: "finish" as const,
    },
    {
      title: "Exonérations",
      description: `-${formatCurrency(
        payslipData.calculation_sequence.exemptions?.totalExemptions || 
        payslipData.deductions.exemptions?.totalExemptions || 0
      )}`,
      status: "finish" as const,
    },
    {
      title: "Abattement Forfaitaire",
      description: `-${formatCurrency(
        payslipData.calculation_sequence.allowanceDeduction
      )}`,
      status: "finish" as const,
    },
    {
      title: "IUTS Net à Payer",
      description: `-${formatCurrency(
        payslipData.calculation_sequence.iutsNetToPay
      )}`,
      status: "finish" as const,
    },
  ];

  const exemptionsData = [
    {
      key: "housing",
      type: "Logement",
      rate: "20%",
      max: "75,000 FCFA",
      amount: payslipData.deductions.exemptions.housingExemption,
    },
    {
      key: "responsibility",
      type: "Responsabilité",
      rate: "5%",
      max: "50,000 FCFA",
      amount: payslipData.deductions.exemptions.responsibilityExemption,
    },
    {
      key: "transport",
      type: "Transport",
      rate: "5%",
      max: "30,000 FCFA",
      amount: payslipData.deductions.exemptions.transportExemption,
    },
  ];

  // Helper function to get data source status
  const getDataSourceStatus = () => {
    if (payslipData.dataSource === 'persisted') {
      return {
        color: 'success' as const,
        text: 'Données validées',
        icon: '✓',
        description: payslipData.persistedAt ? 
          `Validé le ${formatDate(payslipData.persistedAt)}` : 
          'Données officielles persistées'
      };
    } else {
      return {
        color: 'warning' as const,
        text: 'Données recalculées',
        icon: '⚠️',
        description: 'Calcul en temps réel - non persisté'
      };
    }
  };

  const dataStatus = getDataSourceStatus();

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          Bulletin de Paie IUTS - {payslipData.employee.first_name}{" "}
          {payslipData.employee.last_name}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={[
        <Button key="print" icon={<PrinterOutlined />} onClick={handlePrint}>
          Imprimer
        </Button>,
        <Button
          key="download"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
        >
          Télécharger PDF
        </Button>,
        <Button key="close" type="primary" onClick={onCancel}>
          Fermer
        </Button>,
      ]}
    >
      <div id="payslip-content">
        {/* Indicateur de statut des données */}
        <Alert
          message={`${dataStatus.icon} ${dataStatus.text}`}
          description={dataStatus.description}
          type={dataStatus.color}
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* En-tête du bulletin */}
        <Card style={{ marginBottom: 16 }}>
          <Row>
            <Col span={12}>
              <Descriptions
                title="Informations Employé"
                size="small"
                column={1}
              >
                <Descriptions.Item label="Nom complet">
                  <Text strong>
                    {payslipData.employee.first_name}{" "}
                    {payslipData.employee.last_name}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="N° Employé">
                  {payslipData.employee.employee_number}
                </Descriptions.Item>
                <Descriptions.Item label="Département">
                  {payslipData.employee.department_name}
                </Descriptions.Item>
                <Descriptions.Item label="Poste">
                  {payslipData.employee.position_title}
                </Descriptions.Item>
                <Descriptions.Item label="Catégorie">
                  <Tag
                    color={
                      payslipData.employee.professional_category === "cadre"
                        ? "blue"
                        : "green"
                    }
                  >
                    {payslipData.employee.professional_category === "cadre"
                      ? "Cadre"
                      : "Non-cadre"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Charges familiales">
                  {payslipData.employee.dependents_count} personne(s)
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions title="Période de Paie" size="small" column={1}>
                <Descriptions.Item label="Période">
                  <Text strong>{payslipData.period.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Du">
                  {formatDate(payslipData.period.start_date)}
                </Descriptions.Item>
                <Descriptions.Item label="Au">
                  {formatDate(payslipData.period.end_date)}
                </Descriptions.Item>
                <Descriptions.Item label="Entreprise">
                  {payslipData.period.company_name}
                </Descriptions.Item>
                <Descriptions.Item label="Calculé le">
                  {formatDate(payslipData.calculatedAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Version">
                  {payslipData.calculatorVersion}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Card>

        {/* Séquence de calcul Burkina Faso */}
        <Card
          title="Séquence de Calcul selon la Législation Burkina Faso"
          style={{ marginBottom: 16 }}
        >
          <Alert
            message="Calcul selon la législation IUTS du Burkina Faso"
            description="Séquence: Rémunération Totale → CNSS → Salaire Brut → Exonérations → Abattement Forfaitaire → IUTS"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Steps direction="vertical" size="small" items={calculationSteps} />
        </Card>

        {/* Détail des gains */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Card title="💰 Gains" size="small">
              <Statistic
                title="Salaire de Base"
                value={payslipData.earnings.basicSalary}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: "#52c41a" }}
              />
              <Divider />
              <Statistic
                title="Rémunération Totale"
                value={payslipData.earnings.totalRemuneration}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: "#1890ff", fontSize: "18px" }}
              />
              <Text type="secondary" style={{ fontSize: "12px" }}>
                (Salaire + primes + indemnités + avantages + heures sup.)
              </Text>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="🏛️ Cotisations CNSS" size="small">
              <Row gutter={8}>
                <Col span={12}>
                  <Statistic
                    title="Base CNSS"
                    value={payslipData.deductions.cnss.base}
                    formatter={(value) => formatCurrency(Number(value))}
                    valueStyle={{ fontSize: "14px" }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={`Employé (${payslipData.deductions.cnss.employeeRate}%)`}
                    value={payslipData.deductions.cnss.employeeAmount}
                    formatter={(value) => formatCurrency(Number(value))}
                    valueStyle={{ color: "#ff4d4f", fontSize: "14px" }}
                  />
                </Col>
              </Row>
              <Divider />
              <Statistic
                title="Salaire Brut (après CNSS)"
                value={payslipData.earnings.grossSalary}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: "#722ed1", fontSize: "16px" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Exonérations détaillées */}
        <Card
          title="🎯 Exonérations sur Indemnités"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={exemptionsData}
            size="small"
            pagination={false}
            columns={[
              {
                title: "Type d'exonération",
                dataIndex: "type",
                key: "type",
              },
              {
                title: "Taux",
                dataIndex: "rate",
                key: "rate",
                align: "center",
              },
              {
                title: "Plafond",
                dataIndex: "max",
                key: "max",
                align: "center",
              },
              {
                title: "Montant exonéré",
                dataIndex: "amount",
                key: "amount",
                align: "right",
                render: (amount: number) => (
                  <Text style={{ color: amount > 0 ? "#52c41a" : undefined }}>
                    {formatCurrency(amount || 0)}
                  </Text>
                ),
              },
            ]}
          />
          <div style={{ textAlign: "right", marginTop: 8 }}>
            <Text strong>
              Total Exonérations:{" "}
              {formatCurrency(
                payslipData.calculation_sequence.exemptions?.totalExemptions || 0
              )}
            </Text>
            {!payslipData.calculation_sequence.exemptions?.totalExemptions && (
              <div style={{ marginTop: 4 }}>
                <Text type="warning" style={{ fontSize: "11px" }}>
                  ⚠️ Données d'exonérations non chargées
                </Text>
              </div>
            )}
          </div>
        </Card>

        {/* IUTS détaillé */}
        <Card
          title="📊 Calcul IUTS (Impôt Unique sur le Traitement des Salaires)"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Salaire Brut">
                  {formatCurrency(payslipData.earnings.grossSalary)}
                </Descriptions.Item>
                <Descriptions.Item label="Moins: Exonérations">
                  -
                  {formatCurrency(
                    payslipData.calculation_sequence.exemptions?.totalExemptions || 0
                  )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={`Moins: Abattement forfaitaire (${
                    payslipData.employee.professional_category === "cadre"
                      ? "20%"
                      : "25%"
                  })`}
                >
                  -{formatCurrency(payslipData.deductions.allowanceDeduction)}
                </Descriptions.Item>
                <Descriptions.Item label="Salaire Net Imposable">
                  <Text strong style={{ color: "#1890ff" }}>
                    {formatCurrency(
                      payslipData.calculation_sequence.netTaxableIncome
                    )}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: "11px" }}>
                    (Salaire brut - Exonérations - Abattement)
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="IUTS Zéro Charge">
                  {formatCurrency(payslipData.deductions.iuts.iutsZeroCharge)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={`Réduction charges (${payslipData.deductions.iuts.dependentsCount} pers.)`}
                >
                  -
                  {formatCurrency(
                    payslipData.deductions.iuts.dependentsReduction
                  )}{" "}
                  ({Math.round(payslipData.deductions.iuts.reductionRate * 100)}
                  %)
                </Descriptions.Item>
                <Descriptions.Item label="IUTS Net à Payer">
                  <Text strong style={{ color: "#ff4d4f", fontSize: "16px" }}>
                    {formatCurrency(payslipData.deductions.iuts.iutsNetToPay)}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
          
          {/* Détail des tranches IUTS */}
          {payslipData.deductions.iuts.taxDetails && payslipData.deductions.iuts.taxDetails.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Divider orientation="left" orientationMargin="0">
                <Text strong>Détail du calcul par tranches</Text>
              </Divider>
              <Table
                size="small"
                dataSource={payslipData.deductions.iuts.taxDetails}
                pagination={false}
                rowKey={(record, index) => index || 0}
                columns={[
                  {
                    title: 'Tranche (FCFA)',
                    dataIndex: 'bracket',
                    key: 'bracket',
                    width: '30%',
                  },
                  {
                    title: 'Taux',
                    dataIndex: 'rate',
                    key: 'rate',
                    width: '15%',
                    render: (rate: number) => `${rate}%`,
                  },
                  {
                    title: 'Montant imposable',
                    dataIndex: 'taxableAmount',
                    key: 'taxableAmount',
                    width: '25%',
                    render: (amount: number) => formatCurrency(amount),
                  },
                  {
                    title: 'IUTS calculé',
                    dataIndex: 'taxAmount',
                    key: 'taxAmount',
                    width: '25%',
                    render: (amount: number) => formatCurrency(amount),
                  },
                ]}
                summary={(pageData) => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>Total IUTS zéro charge</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <Text strong>
                        {formatCurrency(payslipData.deductions.iuts.iutsZeroCharge)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>
          )}
        </Card>

        {/* Résumé final */}
        <Card title="💸 Résumé du Bulletin" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Salaire Brut"
                value={payslipData.earnings.grossSalary}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: "#52c41a", fontSize: "20px" }}
                prefix={<MoneyCollectOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Déductions"
                value={payslipData.deductions.totalDeductions}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: "#ff4d4f", fontSize: "20px" }}
                prefix={<CalculatorOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Salaire Net"
                value={payslipData.netSalary}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{
                  color: "#1890ff",
                  fontSize: "24px",
                  fontWeight: "bold",
                }}
                prefix={<UserOutlined />}
              />
            </Col>
          </Row>
        </Card>

        {/* Coûts employeur */}
        <Card title="🏢 Coûts Employeur" size="small">
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title="CNSS Employeur (16%)"
                value={payslipData.employerCosts.cnssEmployer}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: "#722ed1" }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="Coût Total Employeur"
                value={payslipData.employerCosts.totalEmployerCost}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: "#fa8c16", fontSize: "16px" }}
              />
            </Col>
          </Row>
        </Card>
      </div>
    </Modal>
  );
};

export default PayslipDetailsModal;
